/** Default site content — mirrors the current live OilOnWheelz copy.
 * Used as fallback when the CMS API is unavailable, and as the seed for the CMS.
 */
const defaultContent = {
  site: {
    name: "OilOnWheelz",
    logo: "/logo-icon.png",
    wordmark: "OILONWHEELZ",
    coalImage: "/coal-bg.png",
    seoTitle: "OilOnWheelz — Used Oil Recovery Network",
    seoDescription:
      "OilOnWheelz connects collection, traceability, and compliant recovery through one used oil recovery network.",
  },

  nav: {
    solutionsLabel: "Solutions",
    connectLabel: "Connect",
    links: [
      { label: "Recovery", href: "/#how-it-works", order: 1, active: true },
      { label: "Network", href: "/#layers", order: 2, active: true },
      { label: "Why Us", href: "/#why-us", order: 3, active: true },
      { label: "Academy", href: "/page/academy", order: 4, active: true },
    ],
    solutions: [
      { label: "OEMs & Producers", href: "/page/oems-producers", order: 1, active: true },
      { label: "Dealerships & Workshops", href: "/page/dealerships-workshops", order: 2, active: true },
      { label: "Industries & Fleets", href: "/page/industries-fleets", order: 3, active: true },
      { label: "Authorised Recyclers", href: "/page/authorised-recyclers", order: 4, active: true },
    ],
    connect: [
      { label: "Contact Us", href: "/#contact", order: 1, active: true },
      { label: "Partner With Us", href: "/#partner", order: 2, active: true },
      { label: "Become a Collection Partner", href: "/#consultation", order: 3, active: true },
    ],
    mobileLinks: [
      { label: "Recovery", href: "/#how-it-works", order: 1, active: true },
      { label: "Network", href: "/#layers", order: 2, active: true },
      { label: "Why Us", href: "/#why-us", order: 3, active: true },
      { label: "Academy", href: "/page/academy", order: 4, active: true },
      { label: "Contact Us", href: "/#contact", order: 5, active: true },
      { label: "Partner With Us", href: "/#partner", order: 6, active: true },
      { label: "Become a Collection Partner", href: "/#consultation", order: 7, active: true },
    ],
  },

  hero: {
    eyebrow: "Traceability for every drop",
    titleLine1: "The Recovery Network",
    titleLine2: "for Your Used Oil",
    body:
      "Used oil moves through a fragmented recovery chain where visibility is often lost. OilOnWheelz connects collection, traceability, and compliant recovery through one network — giving businesses complete oversight from source to recycler.",
    primaryCta: "Schedule a Consultation",
    secondaryCta: "Explore Our Network",
    secondaryHref: "/#layers",
  },

  flowSteps: [
    { title: "CRUDE OIL", desc: "Earth Source", iconKey: "crude" },
    { title: "BASE OIL", desc: "Refined Base", iconKey: "base" },
    { title: "LUBRICANT", desc: "Active Use", iconKey: "lubricant" },
    { title: "USED OIL", desc: "Ready Recovery", iconKey: "used" },
  ],

  problem: {
    heading: "Building a More Self-Reliant Future, One Drop at a Time",
    cards: [
      {
        title: "Where Does It Go?",
        desc: "Once used oil leaves a workshop, dealership, or industrial facility, visibility often becomes limited. As it moves through multiple touchpoints, tracking its journey can become increasingly difficult.",
      },
      {
        title: "Who's Accountable?",
        desc: "Used oil may pass through several stakeholders before reaching its final destination. Without a connected system, accountability can become fragmented and difficult to verify.",
      },
      {
        title: "Can We Prove Recovery?",
        desc: "Responsible recovery depends on more than collection alone. Without end-to-end traceability and documented proof, demonstrating that oil has been recovered through authorised channels becomes a challenge.",
      },
    ],
    stats: [
      { value: "85%", label: "Lost from the system" },
      { value: "10 – 15%", label: "Recovered" },
    ],
  },

  layers: {
    slideDuration: 5500,
    items: [
      {
        tab: "RECOVERY STARTS HERE",
        title: "Catch it before it's lost.",
        copy: "The quality of recovery is decided long before recycling begins. Every drop captured correctly at the source preserves value, strengthens traceability, and keeps the recovery chain intact.",
        media: "/layer-1.gif",
        blocks: [
          {
            title: "Collection Infrastructure",
            desc: "EV-based pickups and specialised tanker fleets enable reliable collection across workshops, industries, and distributed locations.",
          },
          {
            title: "Source-Level Capture",
            desc: "Used oil is collected directly from generation points, helping prevent loss, preserve quality, and ensure responsible handling.",
          },
          {
            title: "Network Efficiency",
            desc: "A hub-and-spoke recovery network enables efficient aggregation, optimised movement, and seamless progression through the recovery journey.",
          },
        ],
      },
      {
        tab: "EVERY DROP LEAVES A TRAIL",
        title: "Oil travels, accountability should too.",
        copy: "As used oil moves through the recovery ecosystem, visibility becomes critical. Every transfer, validation, and movement is tracked to ensure accountability at every stage.",
        media: "/layer-2.gif",
        blocks: [
          {
            title: "Real-Time Visibility",
            desc: "Track every movement from collection to recovery through a connected platform.",
          },
          {
            title: "Source Validation",
            desc: "Verify origin, quantity, and quality to maintain trust throughout the recovery journey.",
          },
          {
            title: "Chain of Custody",
            desc: "Document every handover to ensure transparency, accountability, and traceability for every drop.",
          },
        ],
      },
      {
        tab: "RECOVERY YOU CAN PROVE",
        title: "Compliance is the outcome. Not the process.",
        copy: "When every stage of the recovery journey is connected, traceability, accountability, and compliance become built into the system—not added at the end.",
        media: "/layer-3.gif",
        blocks: [
          {
            title: "Traceability for Every Drop",
            desc: "Every movement is tracked from generation to recovery, creating a complete and verifiable recovery journey.",
          },
          {
            title: "Audit-Ready Records",
            desc: "Time-stamped documentation and verified data provide confidence in every compliance claim.",
          },
          {
            title: "Built-In Accountability",
            desc: "A connected chain of custody ensures every stakeholder, transfer, and recovery event remains visible and accountable.",
          },
        ],
      },
    ],
  },

  impact: {
    headingLine1: "Building a More Self-Reliant Future,",
    headingLine2: "One Drop at a Time",
    intro:
      "Used oil shouldn't be lost to untracked channels. Through organised collection, end-to-end traceability, and compliant recycling, OilOnWheelz helps recover valuable resources, support circular growth, and strengthen India's journey towards greater self-reliance.",
    cards: [
      {
        title: "Creating Impact Beyond Compliance",
        desc: "Every litre of used oil recovered creates value beyond the recovery process itself. By bringing structure, traceability, and accountability to the ecosystem, OilOnWheelz helps businesses contribute to a more circular, efficient, and self-reliant future.",
      },
      {
        title: "Strengthening Self-Reliance",
        desc: "Used oil is a valuable resource. By ensuring it is recovered and routed back into the value chain through authorised recycling, we help reduce the loss of recoverable materials and support a more self-reliant industrial ecosystem.",
      },
      {
        title: "Enabling Circularity",
        desc: "Responsible recovery keeps valuable resources in circulation for longer, reducing waste and supporting the transition towards a more circular economy.",
      },
      {
        title: "Improving Compliance & Transparency",
        desc: "End-to-end traceability provides businesses with greater visibility, audit readiness, and confidence that used oil is being managed responsibly.",
      },
      {
        title: "Reducing Environmental Impact",
        desc: "Structured collection and compliant recycling help reduce dumping, inefficient disposal practices, and unnecessary environmental harm.",
      },
    ],
  },

  numbers: {
    headingLine1: "Your oil becomes",
    headingLine2: "valuable again.",
    sub: "Instead of disappearing into informal channels, your used oil is validated and sent to licensed recyclers—becoming raw material for new base oils and closing the loop.",
    quote:
      '"You get credit for that. It counts toward your ESG targets. It counts toward your EPR compliance."',
    tagline: "This isn't coming. It's happening now.",
    stats: [
      { label: "Oil Collected", num: 5760, suffix: " Tonnes", comma: true, accent: false },
      { label: "Collection Points Active", num: 340, suffix: "+", comma: false, accent: false },
      { label: "Recyclers Connected", num: 18, suffix: "", comma: false, accent: false },
    ],
  },

  whyUs: {
    label: "Why Oil on Wheelz?",
    headingLine1: "Built on real infrastructure,",
    headingLine2: "not just another software.",
    desc: "OilOnWheelz combines logistics, technology, and compliance to bridge a key industry gap—connecting oil generation to responsible recovery through a single, scalable network. Backed by deep sector expertise and built for execution, we turn fragmented operations into a reliable used oil management system.",
    cards: [
      {
        title: "End-to-End Ownership",
        desc: "We manage the entire lifecycle—from collection to recycling and compliance—through one connected system.",
        gradient:
          "radial-gradient(ellipse at 30% 70%, rgba(196,90,26,0.82) 0%, rgba(122,47,8,0.72) 35%, rgba(10,10,10,0.60) 70%)",
        image: "/why-end-to-end.png",
        icon: "/icon-end-to-end.svg",
      },
      {
        title: "Compliance You Can Trust",
        desc: "Built-in EPR tracking, audit trails, ownership traceability, and documentation for transparent, compliant reporting.",
        gradient:
          "radial-gradient(ellipse at 50% 50%, rgba(26,74,110,0.82) 0%, rgba(12,42,66,0.72) 40%, rgba(5,10,16,0.60) 75%)",
        image: "/why-compliance.png",
        icon: "/icon-compliance.svg",
      },
      {
        title: "Infrastructure That Executes",
        desc: "Dedicated collection vehicles, tanker capacity, storage hubs, and recycler linkages built to operate efficiently at scale.",
        gradient:
          "radial-gradient(ellipse at 50% 60%, rgba(184,64,32,0.82) 0%, rgba(110,32,16,0.72) 38%, rgba(8,5,5,0.60) 72%)",
        image: "/why-infrastructure.png",
        icon: "/icon-infrastructure.svg",
      },
      {
        title: "Technology With Real Utility",
        desc: "A platform layer enabling tracking, source mapping, validation, workflows, and ecosystem-wide visibility—beyond just an app.",
        gradient:
          "radial-gradient(ellipse at 60% 40%, rgba(26,58,110,0.82) 0%, rgba(10,30,66,0.72) 40%, rgba(3,6,16,0.60) 75%)",
        image: "/why-technology.png",
        icon: "/icon-technology.svg",
      },
    ],
  },

  howItWorks: {
    eyebrow: "WE MOVE.",
    headingLine1: "The Missing Link in Used",
    headingLine2: "Oil Recovery Gap",
    desc: "The challenge isn't recovery—it's visibility. OilOnWheelz connects every stage of the recovery journey, enabling transparency, accountability, and traceability for every drop.",
    mapImage: "/world-map.svg",
    steps: [
      {
        num: "1",
        title: "Generator",
        desc: "Used oil is generated across workshops, dealerships, and industries. Capturing it correctly is the foundation of responsible recovery.",
        svgX: 130,
        svgY: 270,
        textAbove: false,
      },
      {
        num: "2",
        title: "Collection",
        desc: "Recovery begins with efficient collection at\nthe point\nof generation.",
        svgX: 340,
        svgY: 305,
        textAbove: false,
      },
      {
        num: "3",
        title: "Movement",
        desc: "Every handover remains visible, creating a traceable recovery journey.",
        svgX: 570,
        svgY: 200,
        textAbove: false,
      },
      {
        num: "4",
        title: "Recovery",
        desc: "Recovered oil enters verified recycling channels, enabling responsible resource recovery.",
        svgX: 800,
        svgY: 115,
        textAbove: false,
      },
      {
        num: "5",
        title: "EPR & Compliance",
        desc: "Traceability provides confidence in every compliance claim.",
        svgX: 1010,
        svgY: 40,
        textAbove: false,
      },
    ],
  },

  cta: {
    eyebrow: "JOIN THE CIRCULAR ECONOMY",
    heading: "Ready to close the loop on used oil?",
    sub: "Partner with India's first tech-enabled used oil collection platform — connecting OEMs, dealers, and recyclers into one compliant network.",
    buttonLabel: "Partner with us →",
    video: "/oilGIF.mp4",
  },

  consultationModal: {
    eyebrow: "GET IN TOUCH",
    heading: "Get in Touch",
    sub: "Looking for reliable used oil recovery for your business? Fill in your details and our team will get in touch with you shortly.",
    successHeading: "Request received!",
    successSub: "Our team will reach out to schedule your consultation shortly.",
    submitLabel: "Schedule Consultation →",
    phoneCode: "+91",
    fields: {
      namePlaceholder: "Enter your full name",
      companyPlaceholder: "Enter your company name",
      designationPlaceholder: "Enter your job title / role",
      phonePlaceholder: "Enter your contact number",
      emailPlaceholder: "Enter your work email address",
      briefPlaceholder:
        "Tell us a little about your used oil recovery needs, location, fleet size, or any specific requirement",
    },
  },

  partnerModal: {
    eyebrow: "GET IN TOUCH",
    heading: "Partner with us",
    sub: "Fill in your details and our team will reach out shortly.",
    successHeading: "Thank you!",
    successSub: "We'll be in touch with you shortly.",
    submitLabel: "Submit →",
    phoneCode: "+91",
    fields: {
      namePlaceholder: "Your full name",
      phonePlaceholder: "98765 43210",
      emailPlaceholder: "you@company.com",
    },
  },

  faq: {
    eyebrow: "GOT QUESTIONS",
    headingLine1: "Frequently",
    headingLine2: "Asked Questions",
    items: [
      {
        q: "What is used oil?",
        a: "Used oil is any lubricating oil that has been contaminated through use in engines, machinery, industrial equipment, or hydraulic systems. While no longer fit for its original purpose, it still retains value and can be recovered and recycled.",
      },
      {
        q: "What happens to used lubricating oil after collection?",
        a: "After collection, used oil is transported through a controlled recovery chain, validated, and delivered to authorised recyclers. It is then processed for recovery, recycling, or re-refining in compliance with environmental regulations.",
      },
      {
        q: "What is EPR compliance for used oil?",
        a: "Extended Producer Responsibility (EPR) for used oil requires producers and importers to ensure that a specified quantity of used oil is responsibly recovered and recycled through authorised channels, as mandated by regulatory authorities.",
      },
      {
        q: "Why is used oil recovery important?",
        a: "Used oil is a valuable resource that can be recovered and reused. Proper recovery helps prevent environmental contamination, supports circular resource use, improves traceability, and ensures regulatory compliance.",
      },
      {
        q: "What industries generate used oil?",
        a: "Used oil is generated across multiple sectors, including automotive workshops, dealerships, manufacturing plants, logistics and transport fleets, power generation facilities, mining operations, and industrial processing units.",
      },
      {
        q: "Can used oil be recycled into new base oil?",
        a: "Yes. Through re-refining and authorised recycling processes, used lubricating oil can be converted into high-quality base oil, reducing dependence on virgin resources and supporting a circular economy.",
      },
      {
        q: "Who is required to comply with EPR obligations?",
        a: "EPR obligations generally apply to producers, importers, and brand owners (PIBOs) of lubricating oils. These entities are responsible for ensuring that equivalent quantities of used oil are recovered and recycled through authorised channels.",
      },
      {
        q: "What compliance requirements can OilOnWheelz support?",
        a: "OilOnWheelz supports organisations with used oil recovery, traceability, chain-of-custody documentation, recovery tracking, audit-ready records, and EPR compliance reporting through a connected and transparent recovery ecosystem.",
      },
    ],
  },

  footer: {
    brand: "OILONWHEELZ",
    taglineLine1: "Built for a cleaner,",
    taglineLine2: "more circular oil ecosystem.",
    copyright: "© 2026 OilOnWheelz. All rights reserved.",
    columns: [
      {
        title: "Company",
        links: [
          { label: "About Us", href: "/#about", order: 1, active: true, openInNew: false },
          { label: "How It Works", href: "/#how-it-works", order: 2, active: true, openInNew: false },
          { label: "Why OilOnWheelz", href: "/#why-us", order: 3, active: true, openInNew: false },
          { label: "Contact", href: "/#contact", order: 4, active: true, openInNew: false },
        ],
      },
      {
        title: "Solutions",
        links: [
          { label: "Used Oil Collection", href: "/#how-it-works", order: 1, active: true, openInNew: false },
          { label: "EPR Compliance", href: "/#impact", order: 2, active: true, openInNew: false },
          { label: "Recycling Network", href: "/#numbers", order: 3, active: true, openInNew: false },
          { label: "Liquid Logistics", href: "/#layers", order: 4, active: true, openInNew: false },
        ],
      },
      {
        title: "Resources",
        links: [
          { label: "FAQs", href: "/#faq", order: 1, active: true, openInNew: false },
          { label: "Industry Insights", href: "/#numbers", order: 2, active: true, openInNew: false },
          { label: "Compliance Support", href: "/#partner", order: 3, active: true, openInNew: false },
        ],
      },
      {
        title: "Legal",
        links: [
          { label: "Privacy Policy", href: "/page/privacy-policy", order: 1, active: true, openInNew: false },
          { label: "Terms of Use", href: "/page/terms-of-use", order: 2, active: true, openInNew: false },
        ],
      },
    ],
    legalPages: {
      privacy: {
        title: "Privacy Policy",
        updated: "Last updated: July 2026",
        body: "OilOnWheelz (“we”, “our”, or “us”) respects your privacy.\n\nInformation we collect\nWe may collect contact details you submit through our consultation or partner forms, including name, company, designation, phone number, email address, and any requirements you share.\n\nHow we use information\nWe use this information to respond to enquiries, schedule consultations, evaluate partnership opportunities, and improve our services.\n\nSharing\nWe do not sell your personal information. We may share data with trusted service providers who help us operate our business, subject to appropriate confidentiality obligations, or when required by law.\n\nData security\nWe take reasonable technical and organisational measures to protect personal information against unauthorised access, loss, or misuse.\n\nYour choices\nYou may request access, correction, or deletion of your personal information by contacting us through the website.\n\nContact\nFor privacy-related questions, use the Contact or Partner forms on this website.",
      },
      terms: {
        title: "Terms of Use",
        updated: "Last updated: July 2026",
        body: "By accessing or using the OilOnWheelz website, you agree to these Terms of Use.\n\nWebsite purpose\nThis website provides information about OilOnWheelz used oil recovery, traceability, and compliance services. Content is for general information and does not constitute legal, regulatory, or professional advice.\n\nAcceptable use\nYou agree not to misuse the website, attempt unauthorised access, or submit false or unlawful information through our forms.\n\nIntellectual property\nAll branding, text, graphics, and other materials on this website are owned by OilOnWheelz or its licensors and may not be copied or used without permission.\n\nThird-party links\nAny third-party links are provided for convenience. We are not responsible for external sites or their content.\n\nLimitation of liability\nTo the fullest extent permitted by law, OilOnWheelz is not liable for any indirect or consequential loss arising from use of this website.\n\nChanges\nWe may update these terms from time to time. Continued use of the website after changes constitutes acceptance of the updated terms.\n\nContact\nFor questions about these terms, contact us through the forms on this website.",
      },
    },
  },
};

export default defaultContent;
